/* eslint-disable react-hooks/set-state-in-effect */
import { useEffect, useMemo, useRef, useState } from "react";
import { Icon } from "@/components/icons/Icon";
import { cn } from "@/lib/styles.utils";
import Label from "../label/Label";

export type SelectOption = { label: string; value: string; disabled?: boolean };

interface CustomSelectProps {
  size: "large" | "medium" | "small";
  label?: string;
  placeholder?: string;
  required?: boolean;
  titleInPanel?: string;
  options: SelectOption[];
  value?: string | null;
  onChange?: (value: string) => void;
  disabled?: boolean;
  name?: string;
  error?: boolean;
  id: string;
  children?: React.ReactNode;
}

const InputSelect = ({
  size,
  label,
  placeholder,
  required,
  titleInPanel,
  options,
  value = null,
  onChange,
  disabled,
  name,
  error,
  id,
  children,
}: CustomSelectProps) => {
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const listRef = useRef<HTMLUListElement | null>(null);

  const [open, setOpen] = useState(false);
  const [openUpwards, setOpenUpwards] = useState(false);
  const [highlight, setHighlight] = useState<number>(-1);
  const [internalValue, setInternalValue] = useState<string | null>(value);

  const listboxId = `${id}-listbox`;

  useEffect(() => {
    setInternalValue(value ?? null);
  }, [value]);

  const selectedIndex = useMemo(
    () => options.findIndex((o) => o.value === internalValue),
    [options, internalValue],
  );

  const selectedLabel = selectedIndex >= 0 ? options[selectedIndex].label : "";

  const heights = useMemo(() => {
    if (size === "small") {
      return {
        font: "text-sm",
        optionsGroup: "max-h-60",
        estimatedDropdownHeight: 240,
      };
    }

    if (size === "medium") {
      return {
        font: "text-base",
        optionsGroup: "max-h-64",
        estimatedDropdownHeight: 256,
      };
    }

    return {
      font: "text-lg",
      optionsGroup: "max-h-[272px]",
      estimatedDropdownHeight: 272,
    };
  }, [size]);

  const calculateDropdownDirection = () => {
    if (!buttonRef.current) return;

    const rect = buttonRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;

    const extraPanelHeight = titleInPanel ? 44 : 0;
    const dropdownHeight =
      (listRef.current?.offsetHeight ?? heights.estimatedDropdownHeight) +
      extraPanelHeight +
      8;

    const shouldOpenUpwards =
      spaceBelow < dropdownHeight && spaceAbove > spaceBelow;

    setOpenUpwards(shouldOpenUpwards);
  };

  useEffect(() => {
    if (!open) return;

    calculateDropdownDirection();

    const handleResizeOrScroll = () => {
      calculateDropdownDirection();
    };

    window.addEventListener("resize", handleResizeOrScroll);
    window.addEventListener("scroll", handleResizeOrScroll, true);

    return () => {
      window.removeEventListener("resize", handleResizeOrScroll);
      window.removeEventListener("scroll", handleResizeOrScroll, true);
    };
  }, [open, titleInPanel, heights.estimatedDropdownHeight]);

  useEffect(() => {
    if (!open) return;

    const handler = (ev: MouseEvent) => {
      if (!buttonRef.current && !listRef.current) return;

      const target = ev.target as Node;

      if (
        buttonRef.current?.contains(target) ||
        listRef.current?.contains(target)
      ) {
        return;
      }

      setOpen(false);
      setHighlight(-1);
    };

    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const commitChange = (newVal: string | null) => {
    setInternalValue(newVal);
    onChange?.(newVal as string);
    setOpen(false);
    setHighlight(-1);
  };

  const moveHighlight = (dir: 1 | -1) => {
    if (!open) setOpen(true);

    const len = options.length;
    let next = highlight;

    for (let i = 0; i < len; i++) {
      next = (next + dir + len) % len;
      if (!options[next].disabled) break;
    }

    setHighlight(next);

    requestAnimationFrame(() => {
      const el = document.getElementById(`${id}-option-${next}`);
      el?.scrollIntoView?.({ block: "nearest" });
    });
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (disabled) return;

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        moveHighlight(1);
        break;
      case "ArrowUp":
        e.preventDefault();
        moveHighlight(-1);
        break;
      case "Home":
        e.preventDefault();
        setHighlight(0);
        break;
      case "End":
        e.preventDefault();
        setHighlight(options.length - 1);
        break;
      case "Enter":
      case " ":
        if (open && highlight >= 0) {
          e.preventDefault();
          commitChange(options[highlight].value);
        } else if (!open) {
          e.preventDefault();
          setOpen(true);
        }
        break;
      case "Escape":
        if (open) {
          e.preventDefault();
          setOpen(false);
          setHighlight(-1);
        }
        break;
    }
  };

  return (
    <div className={`flex w-full flex-col gap-1 ${heights.font}`}>
      {label && <Label label={label} id={id} required={required} />}

      <div className="relative">
        <button
          ref={buttonRef}
          id={id}
          name={name}
          type="button"
          disabled={disabled}
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-controls={listboxId}
          onKeyDown={onKeyDown}
          onClick={() => !disabled && setOpen((prev) => !prev)}
          className={cn(
            "group w-full rounded-xs border border-[#E4E4E4] bg-white px-3 py-2 text-left transition-shadow focus:outline-none disabled:cursor-not-allowed disabled:bg-[#F5F5F5] disabled:text-[#9E9E9E]",
            {
              "ring-1 ring-[#D7192D]": error,
              "ring-1 ring-[#B1B1B1]": open,
              "ring-1 ring-transparent": !error && !open,
            },
          )}
        >
          <div className="flex items-center gap-2">
            {children}

            {selectedIndex >= 0 ? (
              <span className="truncate text-[#3D3D3D]">{selectedLabel}</span>
            ) : (
              <span className="truncate text-[#9E9E9E]">{placeholder}</span>
            )}

            <Icon
              name={open ? "fillArrowUp" : "fillArrowDown"}
              className="ml-auto size-4 shrink-0 text-[#D7192D] transition-transform"
            />
          </div>
        </button>

        {open && (
          <div
            className={cn(
              "absolute z-50 w-full overflow-hidden rounded-xs border border-[#EDEDED] bg-white shadow-md",
              {
                "top-full mt-1": !openUpwards,
                "bottom-full mb-1": openUpwards,
              },
            )}
          >
            {titleInPanel && (
              <div className="border-b px-3 py-2 text-[#7A7A7A]">
                {titleInPanel}
              </div>
            )}

            <ul
              ref={listRef}
              id={listboxId}
              role="listbox"
              aria-labelledby={id}
              className={cn(
                "custom-scrollbar overflow-auto",
                heights.font,
                heights.optionsGroup,
              )}
            >
              {options.map((opt, idx) => {
                const selected = idx === selectedIndex;
                const highlighted = idx === highlight;

                const base =
                  "w-full px-3 py-3 text-[#3D3D3D] transition-colors";
                const state = opt.disabled
                  ? "cursor-not-allowed opacity-50"
                  : highlighted
                    ? "bg-[#FDE7EA]"
                    : "hover:bg-[#F7F7F7]";
                const border = "border-t first:border-t-0 border-[#EFEFEF]";

                return (
                  <li
                    id={`${id}-option-${idx}`}
                    key={opt.value}
                    role="option"
                    aria-selected={selected}
                    aria-disabled={opt.disabled || undefined}
                    tabIndex={-1}
                    onMouseEnter={() => !opt.disabled && setHighlight(idx)}
                    onMouseLeave={() => setHighlight(-1)}
                    onClick={() => !opt.disabled && commitChange(opt.value)}
                    className={cn(
                      { "bg-[#FDE7EA]": internalValue === opt.value },
                      base,
                      state,
                      border,
                      "cursor-pointer select-none",
                    )}
                  >
                    <div className="flex items-center gap-2">
                      <span className="truncate">{opt.label}</span>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};

export default InputSelect;
